import React, { useEffect, useState, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { get, isEmpty } from "lodash";
import { roleCheck } from "utils/index";
import Loader from "components/Loader";
import {
  OPERATIONS,
  DRAFTING_3D_MANAGER,
  DRAFTING_MANAGER,
  REGISTERED_SURVEYORS_MANAGER,
  OPERATION_MANAGER,
  ACCOUNTS_MANAGER,
  SURVEYORS_MANAGER,
  DRAFTING_OVERSEAS_MANAGER,
  DRAFTING_3D_OVERSEAS_LEBANON_MANAGER,
  DRAFTING_3D_OVERSEAS_PHILIPPINES_MANAGER,
  DRAFTING_3D_OVERSEAS_ARCHICAD_MANAGER
} from "utils/constants";
import { getBaseUrl, getCurrentRole, getCurrentUser } from "../../../reducers";
import { getTimeSpents } from "../../../../../requesters/timeSpentRequester";
import { getJobService } from "../../../../../requesters/jobServiceRequester";
import TimeSpentTableSimple from "../tabs/TimeSpent/TimeSpentTableSimple";

const TimeSpentConfirmationModal = ({ show, sendTo, resetState, jobServiceRowId }) => {
  const baseUrl = useSelector(getBaseUrl);
  const currentUser = useSelector(getCurrentUser);
  const currentRole = useSelector(getCurrentRole)
  const systemRole = currentUser.attributes.role
  const currentTeamRole = currentUser.attributes.current_team_role
  const isOperations = roleCheck(systemRole, currentRole, OPERATIONS)
  const is3DDraftingManager = roleCheck(systemRole, currentTeamRole, DRAFTING_3D_MANAGER)
  const isDraftingManager = roleCheck(systemRole, currentTeamRole, DRAFTING_MANAGER)
  const isLocalDraftingManager = is3DDraftingManager || isDraftingManager
  const isRsManager = roleCheck(systemRole, currentTeamRole, REGISTERED_SURVEYORS_MANAGER)
  const isOperationManager = roleCheck(systemRole, currentTeamRole, OPERATION_MANAGER)
  const isAccountsManager = roleCheck(systemRole, currentTeamRole, ACCOUNTS_MANAGER)
  const isSurveyorsManager = roleCheck(systemRole, currentTeamRole, SURVEYORS_MANAGER)
  const isOverseasManager = roleCheck(systemRole, currentTeamRole, DRAFTING_OVERSEAS_MANAGER) ||
    roleCheck(systemRole, currentTeamRole, DRAFTING_3D_OVERSEAS_LEBANON_MANAGER) ||
    roleCheck(systemRole, currentTeamRole, DRAFTING_3D_OVERSEAS_PHILIPPINES_MANAGER) ||
    roleCheck(systemRole, currentTeamRole, DRAFTING_3D_OVERSEAS_ARCHICAD_MANAGER)
  const isAnyManager = (isLocalDraftingManager || isRsManager || isOperationManager || isAccountsManager || isSurveyorsManager) && !isOverseasManager
  const [timeSpents, setTimeSpents] = useState([]);
  const [jobService, setJobService] = useState(null);
  const [requesting, setRequesting] = useState(true);
  const [isMissingTimeSpent, setIsMissingTimeSpent] = useState(false);

  const pathSegments = window.location.pathname.split("/");
  const jobServiceId = jobServiceRowId || pathSegments[pathSegments.indexOf("job_services") + 1];
  const missingTimeSpentMessage = "You cannot move a job with 0 mins logged (minimum is 0.25), please log your time.";

  const fetchJobServiceAndTimeSpents = async () => {
    setRequesting(true);
    try {
      const jobServiceRes = await getJobService(baseUrl, { job_service_id: jobServiceId, lazy_photos: true });
      if (jobServiceRes.status === 200) {
        const jobServiceData = jobServiceRes.response.jobService[jobServiceId];
        setJobService(jobServiceData);

        const timeSpentsRes = await getTimeSpents(
          baseUrl,
          jobServiceData.id,
          jobServiceData.type
        );

        if (timeSpentsRes.status === 200) {
          const timeSpentData = get(timeSpentsRes.response, "timeSpent", {});
          let listOfTimeSpent = Object.values(timeSpentData);
          const additionalInfo = timeSpentsRes.response.meta?.additional_info;

          if (!isEmpty(additionalInfo)) {
            listOfTimeSpent = additionalInfo.meta?.ids.map(
              (id) => timeSpentData[id]
            );
          }
          setTimeSpents(listOfTimeSpent);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setRequesting(false);
    }
  };

  const getCurrentUserTotalHours = useMemo(() => {
    if (!jobService || !timeSpents.length) return 0;

    return timeSpents.reduce((total, time) => {
      const { attributes: {
        trackedForDivision, addedByUserId, trackableId, trackableType, trackedHours
      }} = time;
      const isTimeOnJob = trackableType === "JobService" && trackableId == jobServiceId;
      const haveWorkedOnDepartment = isTimeOnJob && addedByUserId == currentUser.id && 
        (trackedForDivision === jobService.attributes.currentDevision || jobService.attributes.currentDevision === "other");

      if (haveWorkedOnDepartment) {
        return total + parseFloat(trackedHours || 0);
      }
      return total;
    }, 0);
  }, [timeSpents, jobService, jobServiceId, currentUser.id]);

  const isExempt = useMemo(() => {
    if (["admin", "super_admin"].includes(systemRole)) return true;
    if (isOperations) return true;
    if (is3DDraftingManager) return true;
    if (isOverseasManager) return false;
    if (isAnyManager) return true;
    
    return false;
  }, [systemRole, isOperations, is3DDraftingManager, isOverseasManager, isAnyManager]);

  const canSend = useMemo(() => {
    return isExempt || getCurrentUserTotalHours >= 0.25;
  }, [isExempt, getCurrentUserTotalHours]);

  const validateTimeEntry = () => {
    if (isExempt) return true;
    return getCurrentUserTotalHours >= 0.25;
  };

  const onValidateTimeEntry = () => {
    if (validateTimeEntry()) {
      sendTo();
      resetState();
    } else {
      setIsMissingTimeSpent(true);
      toast.error(
        <span className="ml-2">{missingTimeSpentMessage}</span>,
        { toastId: "container-inner", containerId: "main" }
      )
    }
  };

  useEffect(() => {
    if (show && jobServiceId) {
      fetchJobServiceAndTimeSpents();
    }
  }, [show, jobServiceId])

  useEffect(() => {
    if (isMissingTimeSpent) {
      const isMissingTime = !(isExempt || getCurrentUserTotalHours >= 0.25);
      setIsMissingTimeSpent(isMissingTime);
    }
  }, [timeSpents, isMissingTimeSpent, isExempt, getCurrentUserTotalHours])

  return (
    <Modal show={show} onHide={resetState} centered className="time-spent-confirmation-modal">
      <div className="modal-content">
        <div className="modal-header border-0 d-flex justify-content-center">
          <AiOutlineInfoCircle style={{ fontSize: "80px", color: "rgb(70, 184, 218)" }} />
        </div>
        <div className="modal-body text-center">
          <p style={{ fontSize: "18px", fontWeight: "normal" }}>
            Please Confirm Your Time Spent is Correct
          </p>
          {requesting ? (
            <Loader />
          ) : (
            <TimeSpentTableSimple
              timeSpents={timeSpents}
              jobService={jobService}
              reloadData={fetchJobServiceAndTimeSpents}
            />
          )}
          { isMissingTimeSpent && (
            <p className="warning-dark-text py-2 px-3 small">{missingTimeSpentMessage}</p>
          )}
        </div>
        <div className="modal-footer d-flex justify-content-center border-0 mt-4">
          <Button variant="light" onClick={resetState}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={onValidateTimeEntry}
            disabled={!canSend || requesting}
          >
            Send
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TimeSpentConfirmationModal;
